import { useEffect, useRef, useState } from "react";
import { CheckCircle } from "@phosphor-icons/react";
import { QUALS, HANDSHAKE } from "./constants";
import SupportiveFacts from "./SupportiveFacts";

export default function Qualification() {
  const [qualsVisible, setQualsVisible] = useState(false);
  const qualsRef = useRef(null);

  // Trigger the qualification-checkmark sequenced pop animation when the section scrolls into view
  useEffect(() => {
    if (!qualsRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setQualsVisible(true); obs.disconnect(); } },
      { threshold: 0.25 }
    );
    obs.observe(qualsRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="qualify" className="py-16 md:py-20 bg-cvc-parchment/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs tracking-[0.3em] uppercase font-body font-semibold text-cvc-red">Who we work with</p>
          <h2 className="font-heading text-4xl md:text-5xl text-cvc-navy mt-4 leading-tight">
            Are you the kind of owner we can take all the way?
          </h2>
          <p className="font-body text-cvc-ink-soft text-lg mt-6 leading-relaxed">
            We're selective about who we take on — for everyone's sake. Scaling up only works when the foundation is already there.
            You don't need to be perfect; you do need to fit a handful of criteria.
          </p>
          <img src={HANDSHAKE} alt="Jonathan on-site with a Cache Valley client team." className="mt-8 w-full aspect-[4/3] object-cover rounded-2xl" />
        </div>
        <div className="lg:col-span-7 flex flex-col h-full">
          <div className="rounded-2xl bg-white border border-cvc-border p-8 md:p-10">
            <p className="font-heading text-2xl text-cvc-navy">A prepared-client looks like this:</p>
            <ul ref={qualsRef} className="mt-6 space-y-4">
              {QUALS.map((q, i) => (
                <li key={q} data-testid={`qual-${i}`} className="flex gap-4 items-start">
                  <CheckCircle
                    size={38}
                    weight="fill"
                    className={`qual-check ${qualsVisible ? "qual-pop" : ""} shrink-0 mt-0.5`}
                    style={{ color: "#16A34A", animationDelay: `${i * 1000}ms` }}
                  />
                  <p className="font-body text-cvc-ink text-base md:text-lg leading-relaxed pt-1.5">{q}</p>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-cvc-border">
              <p className="font-body text-sm text-cvc-ink-soft">
                Don't tick every box? You can still sign up — Jonathan reviews each case and may suggest some preparations
                before bringing you on as a client.
              </p>
            </div>
          </div>
          <div className="mt-auto pt-6">
            <SupportiveFacts />
          </div>
        </div>
      </div>
    </section>
  );
}
